import React from "react";
import {
  CancelIcon,
  ClockIcon,
  LocationIcon,
  TickIcon,
  VideoIcon,
} from "../../../components/ui";
import {
  lockedProps,
  type GateProps,
} from "../../billing/hooks/useSubscriptionGate";
import {
  appointmentTime,
  attendeeName,
  durationLabel,
} from "../../appointments/appointments";
import { agendaLabel } from "../../appointments/meetingAgenda";
import type { ClientAppointment } from "../../appointments/types/appointments.types";
import { dayParts } from "../myTask";
import type { TaskState } from "../myTask";

/** Whether the row's time has been and gone, however it went. */
const isBehind = (state: TaskState): boolean =>
  state === "Completed" || state === "Cancelled" || state === "Awaiting";

interface MobileTaskCardProps {
  appointment: ClientAppointment;
  state: TaskState;
  /** Off, with its reason, while the workspace has no plan behind it. */
  gate: GateProps;
  onOpen: (appointment: ClientAppointment) => void;
  onComplete: (appointment: ClientAppointment) => void;
  onCancel: (appointment: ClientAppointment) => void;
}

/**
 * One appointment, as a phone draws it: what kind of thing it is and when,
 * then who it is with, then the facts about it, then whatever there is to do
 * about it.
 *
 * Four lines down rather than the desktop's four columns across, which is the
 * whole difference between the two. That row reads left to right — mark, time,
 * kind, name, facts, action — because it has 900px to lay them out in and an
 * eye scanning a table wants its columns to line up. At 360px the same six
 * things have to be read top to bottom, so the kind and the hour pair off on
 * one line (they are both "what slot is this"), the name takes a line of its
 * own as the thing the card is actually about, and the facts run under it.
 *
 * The hour carries a clock glyph here and does not on the desktop. There it
 * sits in a column of nothing but times with a mark beside it; here it is a
 * figure at the end of a line, and the glyph is what makes it read as a time
 * at a glance rather than as a number.
 */
const MobileTaskCard: React.FC<MobileTaskCardProps> = ({
  appointment,
  state,
  gate,
  onOpen,
  onComplete,
  onCancel,
}) => {
  const behind = isBehind(state);
  const cancelled = state === "Cancelled";
  // A way in is only worth offering while there is something to go in to — the
  // same reading the desktop row takes.
  const joinable =
    !behind && appointment.type === "Online" && !!appointment.meetingUrl;
  const current = state === "InProgress";
  const TypeIcon = appointment.type === "Online" ? VideoIcon : LocationIcon;
  // Whoever the hour is with — a client or an enquiry. Read through the one
  // helper rather than off `clientName`, which is `null` on every row booked
  // against an enquiry.
  const name = attendeeName(appointment) || "Unnamed client";
  const agenda = agendaLabel(appointment.agenda);
  const time = appointmentTime(appointment.startsAt);

  /**
   * What there is to do about this hour, or nothing.
   *
   * Three answers and they are mutually exclusive, which is why this is one
   * expression rather than three conditionals in the markup. An hour that is
   * over and unmarked is a question — the two answers to it, the same pair the
   * payment history and the client's own table wear. An hour still to come
   * that is a video call is a way in. Everything else is settled and the card
   * ends at its facts.
   */
  const actions =
    state === "Awaiting" ? (
      <>
        {/* Both sized to what they are rather than stretched across the card,
            which is how the design draws them: a square for the cross, the
            label's own width for the tick, and the rest of the row left empty.
            Stretching the pair to the full width made an answer about one
            appointment look like the card's own footer — a bar across the
            bottom of it — and put the tick's edge under the thumb that was
            reaching for nothing in particular. Left-aligned, they read as two
            buttons on a card instead. */}
        <button
          type="button"
          aria-label={`Cancel the ${time} appointment with ${name} — the session did not take place`}
          {...lockedProps(gate, () => onCancel(appointment))}
          className={`flex h-11 w-[52px] flex-shrink-0 items-center justify-center rounded-[8px] border border-[#F5B5B1] bg-white text-[#FF3B30] transition-colors active:bg-[#FDECEC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF3B30]/40 ${
            gate.disabled ? "cursor-not-allowed opacity-50" : ""
          }`}
        >
          <CancelIcon className="h-5 w-5" strokeWidth={2} />
        </button>
        <button
          type="button"
          {...lockedProps(gate, () => onComplete(appointment))}
          className={`flex h-11 flex-shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-[8px] border border-brand bg-brand-soft px-4 text-[14px] font-medium text-[#007E8C] transition-colors active:bg-[#D2F5F7] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
            gate.disabled ? "cursor-not-allowed opacity-50" : ""
          }`}
        >
          <TickIcon className="h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
          Mark as done
          <span className="sr-only">{` — the ${time} session with ${name} took place`}</span>
        </button>
      </>
    ) : joinable ? (
      // A link and not a button: this goes somewhere, so it opens in a new tab
      // on a long press and is announced as the way out that it is. Filled on
      // the appointment that is happening now and tinted on the ones still to
      // come — on a day with four calls in it, exactly one of them is the one
      // to be in.
      <a
        href={appointment.meetingUrl!}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex h-11 w-full items-center justify-center rounded-[8px] text-[14px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
          current
            ? "bg-brand text-white active:bg-brand-hover"
            : "border border-brand bg-brand-soft text-[#007E8C] active:bg-[#D2F5F7]"
        }`}
      >
        Join
        <span className="sr-only">{` the ${time} appointment with ${name}`}</span>
      </a>
    ) : null;

  return (
    // The gutter is on the card rather than on what is inside it, so the
    // hairline over the actions stops where the text does — a rule running the
    // full width of a card cuts it in two, where this one only sets the
    // actions apart from the facts above them.
    <article
      className={`card-surface border border-line px-4 ${
        behind ? "bg-[#FCFDFD]" : "bg-white"
      }`}
    >
      {/* Everything but the actions is one press, which opens the dialog. One
          button rather than a clickable card, so the row is reachable from a
          keyboard and announced as the one thing it does — and so the tick,
          the cross and the Join under it are not buttons inside a button,
          which is not a thing a browser will render. */}
      <button
        type="button"
        onClick={() => onOpen(appointment)}
        className="block w-full py-3.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand/40"
      >
        {/* Spans rather than divs and paragraphs all the way down this
            button: its content model is phrasing content, and a browser
            handed a `<p>` inside a `<button>` closes the button around it.
            `block` and `flex` do the layout the elements would have. */}
        <span className="flex items-center justify-between gap-3">
          {/* What kind of thing this is. Violet, which is the one colour on
              this screen that is not the brand teal: it marks the row's kind
              rather than its state. A called-off row goes grey with the rest
              of itself — the kind of thing it was stopped mattering when it
              stopped happening. */}
          <span
            className={`inline-flex flex-shrink-0 rounded-[6px] px-2.5 py-1 text-[12px] font-medium ${
              cancelled
                ? "bg-[#F1F3F5] text-[#9AA4AE]"
                : "bg-[#F7F1FF] text-[#7C5CFC]"
            }`}
          >
            Meeting
          </span>
          <span
            className={`flex flex-shrink-0 items-center gap-1.5 text-[13px] font-medium ${
              behind ? "text-ink-muted" : "text-[#00424B]"
            }`}
          >
            <ClockIcon className="h-4 w-4 flex-shrink-0 text-ink-subtle" />
            {time}
          </span>
        </span>

        {/* The rule is for a called-off appointment and nothing else. An hour
            that has simply been and gone still happened, and striking a
            person's name through for it reads as the person being struck out
            rather than the hour. */}
        <span
          className={`mt-2 block truncate text-[16px] font-semibold ${
            cancelled
              ? "text-ink-muted line-through"
              : behind
              ? "text-ink-muted"
              : "text-ink"
          }`}
        >
          {name}
        </span>

        {/* How long it runs, what it is for, where it happens, and — where it
            was called off — why. One line of facts separated by dots. Only the
            agenda and the reason can run long, so they are the two allowed to
            truncate. */}
        <span className="mt-1 flex min-w-0 items-center gap-1.5 text-[13px] text-ink-muted">
          <span className="flex-shrink-0">
            {durationLabel(appointment.durationMinutes)}
          </span>

          {agenda && (
            <>
              <span aria-hidden="true" className="flex-shrink-0 text-ink-subtle">
                •
              </span>
              <span className="truncate">{agenda}</span>
            </>
          )}

          <span aria-hidden="true" className="flex-shrink-0 text-ink-subtle">
            •
          </span>
          <span className="flex flex-shrink-0 items-center gap-1">
            <TypeIcon aria-hidden="true" className="h-3.5 w-3.5 flex-shrink-0" />
            {appointment.type}
          </span>

          {cancelled && appointment.cancelReason && (
            <>
              <span aria-hidden="true" className="flex-shrink-0 text-ink-subtle">
                •
              </span>
              <span className="truncate">{appointment.cancelReason}</span>
            </>
          )}
        </span>
      </button>

      {actions && (
        <div className="flex items-center gap-2.5 border-t border-line py-3">
          {actions}
        </div>
      )}
    </article>
  );
};

export interface MobileTaskListProps {
  /** The day, soonest first. Already sorted — this groups it as given. */
  appointments: ClientAppointment[];
  /** Each row's state, by appointment id — worked out once by the page. */
  states: Map<string, TaskState>;
  /**
   * The page's own clock, so the headings and the cards cannot disagree about
   * how far through the day the practice is.
   */
  now: Date;
  gate: GateProps;
  onOpen: (appointment: ClientAppointment) => void;
  onComplete: (appointment: ClientAppointment) => void;
  onCancel: (appointment: ClientAppointment) => void;
}

/**
 * The day on a phone: Past, Ongoing and Upcoming, with the appointments of
 * each under its heading.
 *
 * In the order it will be lived in, always, and the headings are what the
 * desktop's thread does in a column a phone has not got — see `dayParts`,
 * which splits the day by the clock and drops the stretches with nothing in
 * them.
 *
 * Ongoing is the one heading drawn loud. It is the answer to the question
 * somebody has opened their phone between two appointments to ask, and the
 * other two are only there to place it.
 */
const MobileTaskList: React.FC<MobileTaskListProps> = ({
  appointments,
  states,
  now,
  gate,
  onOpen,
  onComplete,
  onCancel,
}) => (
  <div className="flex flex-col gap-5">
    {dayParts(appointments, now).map((group) => (
      <section key={group.id} aria-label={`${group.title} appointments`}>
        <h3
          className={`text-[15px] ${
            group.id === "ongoing"
              ? "font-semibold text-[#007E8C]"
              : "text-ink-muted"
          }`}
        >
          {group.title}
        </h3>
        <ol className="mt-2 flex flex-col gap-3">
          {group.appointments.map((appointment) => (
            <li key={appointment.id}>
              <MobileTaskCard
                appointment={appointment}
                state={states.get(appointment.id) ?? "Upcoming"}
                gate={gate}
                onOpen={onOpen}
                onComplete={onComplete}
                onCancel={onCancel}
              />
            </li>
          ))}
        </ol>
      </section>
    ))}
  </div>
);

export default MobileTaskList;
